import { useState, useEffect } from "react";

export function useLocalStorageState(key, defaultValue) {
  const [state, setState] = useState(() => {
    const json = localStorage.getItem(key);
    return json != null ? JSON.parse(json) : defaultValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  return [state, setState];
}

export function parseFlags(csvString = "") {
  if (typeof csvString !== "string") {
    console.error("Expected a string, but received:", typeof csvString);
    return {}; // Return an empty object if it's not a string
  }

  return csvString
    .split(",") // Split string by commas
    .map((s) => s.trim()) // Trim spaces around each value
    .filter((s) => s) // Remove empty strings
    .reduce((acc, v) => {
      acc[v] = true; // Set each value to true in the accumulator object
      return acc;
    }, {});
}

export const MODEL_GROUPS = [
  {
    key: "panel1",
    title: "OpenAI Models",
    proOnly: false,
    models: [
      { label: "gpt-4o-mini", onChangeName: "gpt-4o-mini", proOnly: false },
      { label: "gpt-4o", onChangeName: "gpt-4o", proOnly: true },
      { label: "o1", onChangeName: "o1", proOnly: true },
      { label: "o1-mini", onChangeName: "o1-mini", proOnly: true },
      { label: "gpt-4.1", onChangeName: "gpt-4.1-2025-04-14", proOnly: true },
      {
        label: "gpt-4.1-mini",
        onChangeName: "gpt-4.1-mini-2025-04-14",
        proOnly: true,
      },
      {
        label: "gpt-4.1-nano",
        onChangeName: "gpt-4.1-nano-2025-04-14",
        proOnly: true,
      },
      {
        label: "o3",
        onChangeName: "o3-2025-04-16",
        proOnly: true,
      },
      {
        label: "o3-mini",
        onChangeName: "o3-mini-2025-01-31",
        proOnly: true,
      },
      {
        label: "o4-mini",
        onChangeName: "o4-mini-2025-04-16",
        proOnly: true,
      },
    ],
  },
  {
    key: "panel2",
    title: "Meta AI Models",
    proOnly: true,
    models: [
      {
        label: "Meta-Llama-3.1-8B-Instruct-Turbo",
        onChangeName: "meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo",
        proOnly: false,
      },
      {
        label: "Meta-Llama-3.1-405B-Instruct-Turbo",
        onChangeName: "meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo",
        proOnly: true,
      },
      {
        label: "Meta-Llama-3.2-11B-Vision-Instruct-Turbo",
        onChangeName: "meta-llama/Llama-3.2-11B-Vision-Instruct-Turbo",
        proOnly: true,
      },
      {
        label: "Meta-Llama-3.2-90B-Vision-Instruct-Turbo",
        onChangeName: "meta-llama/Llama-3.2-90B-Vision-Instruct-Turbo",
        proOnly: true,
      },
      {
        label: "Meta-Llama-3.3-70B-Instruct-Turbo",
        onChangeName: "meta-llama/Llama-3.3-70B-Instruct-Turbo",
        proOnly: true,
      },
      {
        label: "Meta-Llama-4-Maverick",
        onChangeName: "meta-llama/Llama-4-Maverick-17B-128E-Instruct-FP8",
        proOnly: true,
      },
      {
        label: "Meta-Llama-4-Scout",
        onChangeName: "meta-llama/Llama-4-Scout-17B-16E-Instruct",
        proOnly: true,
      },
    ],
  },
  {
    key: "panel3",
    title: "Anthropic Models",
    proOnly: true,
    models: [
      {
        label: "Claude 3 Opus",
        onChangeName: "claude-3-opus-20240229",
        proOnly: true,
      },
      {
        label: "Claude 3 Sonnet",
        onChangeName: "claude-3-sonnet-20240229",
        proOnly: true,
      },
      {
        label: "Claude 3.5 Haiku",
        onChangeName: "claude-3-5-haiku-20241022",
        proOnly: true,
      },
      {
        label: "Claude 3.5 Sonnet",
        onChangeName: "claude-3-5-sonnet-20241022",
        proOnly: true,
      },
      {
        label: "Claude 3.7 Sonnet",
        onChangeName: "claude-3-7-sonnet-20250219",
        proOnly: true,
      },
      {
        label: "Claude 4 Opus",
        onChangeName: "claude-opus-4-20250514",
        proOnly: true,
      },
      {
        label: "Claude 4 Sonnet",
        onChangeName: "claude-sonnet-4-20250514",
        proOnly: true,
      },
    ],
  },
  {
    key: "panel4",
    title: "Google AI Models",
    proOnly: true,
    models: [
      {
        label: "gemini 1.5 pro",
        onChangeName: "gemini-1.5-pro",
        proOnly: true,
      },
      {
        label: "gemini 2.0 flash",
        onChangeName: "gemini-2.0-flash",
        proOnly: true,
      },
      {
        label: "gemini 2.5 pro preview",
        onChangeName: "gemini-2.5-pro-preview-05-06",
        proOnly: true,
      },
      {
        label: "gemini 2.5 flash preview",
        onChangeName: "gemini-2.5-flash-preview-05-20",
        proOnly: true,
      },
    ],
  },
  {
    key: "panel5",
    title: "Mistral AI Models",
    proOnly: true,
    models: [
      {
        label: "Mixtral 8 22b",
        onChangeName: "mistralai/Mixtral-8x22B-Instruct-v0.1",
        proOnly: true,
      },
      {
        label: "Mixtral 8 7B",
        onChangeName: "mistralai/Mixtral-8x7B-Instruct-v0.1",
        proOnly: true,
      },
    ],
  },
  {
    key: "panel16",
    title: "Qwen Models",
    proOnly: true,
    models: [
      {
        label: "Qwen3 235B A22B fp8 tput",
        onChangeName: "Qwen/Qwen3-235B-A22B-fp8-tput",
        proOnly: true,
      },
    ],
  },
  {
    key: "panel17",
    title: "Deepseek Models",
    proOnly: true,
    models: [
      {
        label: "DeepSeek-V3",
        onChangeName: "deepseek-ai/DeepSeek-V3",
        proOnly: true,
      },
      {
        label: "Deepseek R1",
        onChangeName: "deepseek-ai/DeepSeek-R1",
        proOnly: true,
      },
    ],
  },

  {
    key: "panel18",
    title: "Grok Models",
    proOnly: true,
    models: [
      {
        label: "Grok 3",
        onChangeName: "grok-3-latest",
        proOnly: true,
      },
      {
        label: "Grok 3 Fast",
        onChangeName: "grok-3-fast-latest",
        proOnly: true,
      },
      {
        label: "Grok 3 Mini",
        onChangeName: "grok-3-mini-latest",
        proOnly: true,
      },
      {
        label: "Grok 3 Mini Fast",
        onChangeName: "grok-3-mini-fast-latest",
        proOnly: true,
      },
    ],
  },
];

export function persist(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

// single place to update this node’s part of botData.workflow
export function updateNode(updates, setBotData, nodeId) {
  setBotData((prev) => ({
    ...prev,
    workflow: {
      ...prev.workflow,
      [nodeId]: {
        ...prev.workflow[nodeId],
        ...updates,
      },
    },
  }));
}

// Consolidate state update function
export const updateStateFromLocalStorage = (key, setter) => {
  const storedValue = localStorage.getItem(key);
  if (storedValue) {
    const parsedValue = JSON.parse(storedValue);
    setter(parsedValue);
  }
};
